/* eslint-disable no-param-reassign */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppThunk } from '../store'
import { GetMeType, refresh as refreshApi, getMe } from '../api/account'
import TokenUtils from '../utils/tokenUtils'
import { JwtPayload } from 'jwt-decode'

interface State {
  isLoadingAuth: boolean
  authToken: string | null
  authTokenData: JwtPayload | null

  currentUser: GetMeType | null
  isLoading: boolean
  error: string | null
}

const initialState: State = {
  isLoadingAuth: true,
  authToken: null,
  authTokenData: null,

  currentUser: null,
  isLoading: false,
  error: null,
}

function startLoading(state: State): void {
  state.isLoading = true
}

function loadingFailed(state: State, action: PayloadAction<string>): void {
  state.isLoading = false
  state.error = action.payload
}

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    initializeAuthStart(state: State) {
      state.isLoadingAuth = true
    },
    initializeAuthCompleted(state: State, action: PayloadAction<string | null>) {
      state.authToken = action.payload
      state.authTokenData = TokenUtils.getDecodedAccessToken(action.payload)
      state.isLoadingAuth = false
    },
    getMeStart: startLoading,
    getMeSuccess(state, { payload }: PayloadAction<GetMeType>): void {
      state.currentUser = payload
      state.isLoading = false
      state.error = null
    },
    getMeFailure: loadingFailed,
  },
})

export const { getMeStart, getMeSuccess, getMeFailure, initializeAuthStart, initializeAuthCompleted } = slice.actions

export default slice.reducer

export const initializeAuth =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(initializeAuthStart())
    const accessToken = TokenUtils.getAccessToken()
    dispatch(initializeAuthCompleted(accessToken))

    if (accessToken) {
      dispatch(fetchMe())
    }
  }

export const fetchMe =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      dispatch(getMeStart())
      const response = await getMe()
      dispatch(getMeSuccess(response.data))
    } catch (err) {
      dispatch(getMeFailure(err.toString()))
    }
  }

export const refresh =
  (): AppThunk<Promise<string>> =>
  async (dispatch): Promise<string> => {
    const token = TokenUtils.getAccessToken()

    if (token === null) {
      throw new Error()
    }

    const response = await refreshApi({ token })

    TokenUtils.storeAccessToken(response.data.token)

    dispatch(initializeAuthCompleted(response.data.token))

    return response.data.token
  }

export const logout = (): AppThunk<Promise<void>> => async (): Promise<void> => {
  TokenUtils.unsetToken()

  return Promise.resolve()
}
